/**
 * Where does an Overdue card's action actually get done?
 *
 * The Overdue tab names what is blocking a candidate ("Needs a case panel") and then left the
 * recruiter to find the place that unblocks it — the right tab, the right round, the right role
 * accordion, scrolled to the right card (Theresa, Sep 2026). This turns each stuck state into a
 * destination so the chip itself can be the link.
 *
 * Two rules, and both exist to avoid a link that lands nowhere:
 *
 *  - **The CLASSIFICATION decides whether there is a destination at all.** Some stuck states have
 *    no other place to go: `feedback-missing` is chased with the Nudge button on the card itself,
 *    a `snoozed` candidate is parked on purpose, and `booked` is not waiting on anything. Those
 *    stay plain labels — a link that arrives somewhere with nothing to do reads as a bug.
 *  - **The BOARD decides which round.** A destination is returned only when the candidate is
 *    actually in that section of the payload, so the chip cannot send someone to a tab that does
 *    not list them. The stage name is deliberately not parsed: the sections already know.
 */
import type { RoleBoard, SlaClassification } from '../../types/taActionBoard';

export type SchedulingRound = 'first' | 'case' | 'final';

export type BoardDestination =
  | { tab: 'feedback' }
  | { tab: 'scheduling'; round: SchedulingRound };

/**
 * What KIND of place resolves this state.
 *
 * `'decision'` mirrors `classifySlaCandidate`'s own precedence — a pending decision outranks
 * anything scheduling — and `'scheduling'` means "wherever the board is already showing them".
 */
function placeFor(classification: SlaClassification): 'decision' | 'scheduling' | null {
  switch (classification) {
    case 'blocked-by-decision':
      return 'decision';
    // All four are resolved by scheduling this candidate: booking the round, chasing a reply to
    // an invitation already out, or proposing again after an offer went cold.
    case 'needs-case-panel':
    case 'no-next-step':
    case 'invited-no-reply':
    case 'awaiting-candidate':
      return 'scheduling';
    // feedback-missing → the Nudge button on this very card. snoozed → parked deliberately.
    // booked → nothing is due. None of them has a second place to be.
    case 'feedback-missing':
    case 'snoozed':
    case 'booked':
      return null;
    default:
      return null;
  }
}

/** Which scheduling round is this candidate in on this role's board? null = none of them. */
export function roundFor(role: RoleBoard, opportunityId: string): SchedulingRound | null {
  const has = (list: { opportunityId: string }[] | undefined) =>
    !!list?.some((c) => c.opportunityId === opportunityId);
  if (has(role.firstInterviews?.candidates)) return 'first';
  if (has(role.scheduling?.candidates)) return 'case';
  if (has(role.finalRounds?.candidates)) return 'final';
  return null;
}

/**
 * The destination for one overdue card, or null when the chip should stay a plain label.
 *
 * Null is a real and common answer: a state with no elsewhere to go, or a candidate the board is
 * not listing in any section (which happens — the SLA list and the round sections are built from
 * different predicates, and a gap between them must read as "no link", never as a wrong one).
 */
export function destinationFor(
  role: RoleBoard,
  opportunityId: string,
  classification: SlaClassification
): BoardDestination | null {
  const place = placeFor(classification);
  if (place === 'decision') {
    return role.feedback?.some((f) => f.opportunityId === opportunityId) ? { tab: 'feedback' } : null;
  }
  if (place === 'scheduling') {
    const round = roundFor(role, opportunityId);
    return round ? { tab: 'scheduling', round } : null;
  }
  return null;
}

/** What the link promises, read out to screen readers and shown on hover. */
export function destinationLabel(dest: BoardDestination): string {
  if (dest.tab === 'feedback') return 'Go to the decision';
  return {
    first: 'Go to First interviews',
    case: 'Go to Case scheduling',
    final: 'Go to Final rounds',
  }[dest.round];
}
